<template>
  <v-flex xs12>
    <v-toolbar flat>
      <v-toolbar-title>Projekte</v-toolbar-title>
      <v-spacer />

      <!-- edit -->
      <v-dialog
        v-model="showEditDialog"
        :max-width="dialogWidth"
        persistent
      >
        <v-card>
          <v-card-title>
            <span
              v-if="selectedObject.id !== null"
              class="headline"
            >Bearbeiten: {{ selectedObject.name }}</span>
            <span
              v-else-if="!!selectedObject.name"
              class="headline"
            >Neues Projekt: {{ selectedObject.name }}</span>
            <span
              v-else
              class="headline"
            >Neues Projekt</span>
          </v-card-title>
          <v-card-text>
            <v-form @submit.prevent="submitEdit">

              <v-menu
                    :close-on-content-click="false"
                    bottom
                    offset-y
                    open-on-click
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="selectedObject.name"
                        type="text"
                        label="Name"
                        :disabled="saving"
                        :error-messages="getError('name')"
                        :hint="similarItems.length ? 'Es wurden Projekte mit ähnlichem Namen gefunden.' : ''"
                        persistent-hint
                      >
                        <template v-slot:message="props">
                          <v-btn v-if="similarItems.length" v-on="on" x-small text >
                             <v-icon left>mdi-alert-circle-outline</v-icon>
                              {{ props.message }}
                          </v-btn>
                        </template>
                      </v-text-field>

                    </template>

                    <v-card v-if="similarItems.length" id="similarItems">
                      <v-list>
                        <v-list-item v-for="p in similarItems" :key="p.id">
                          <v-list-item-title>{{p.name}}</v-list-item-title>
                          <v-list-item-action class="ma-0">
                            <v-btn
                              icon
                              title="Bearbeiten"
                              :disabled="loading"
                              @click="openEditDialog(p)"
                            >
                              <v-icon small>
                                mdi-pencil
                              </v-icon>
                            </v-btn>
                          </v-list-item-action>
                        </v-list-item>
                      </v-list>

                    </v-card>
                  </v-menu>

              <v-checkbox
                v-model="selectedObject.inactive"
                label="Inaktiv"
                :disabled="saving"
                :error-messages="errors.inactive"
              />
              <Multiselect
                v-model="selectedObject.employees"
                :customLabel="getEmployeeLabel"
                multiple
                :options="allEmployees"
                track-by="id"
                :loading="employeesLoading"
                :close-on-select="false"
                placeholder="Mitarbeiter auswählen"
                select-label="Mit Eingabe-Taste auswählen"
                selected-label="Ausgewählt"
                deselect-label="Mit Eingabe-Taste abwählen"
                :disabled="ignore_project_operation_assignment"
              >
                <template v-slot:noResult>
                  Keine Suchergebnisse.
                </template>
                <template v-slot:noOptions>
                  Liste ist leer.
                </template>
              </Multiselect>
              <Multiselect
                v-model="selectedObject.operations"
                label="name"
                multiple
                :options="allOperations"
                track-by="id"
                :loading="operationsLoading"
                :close-on-select="false"
                placeholder="Tätigkeiten auswählen"
                select-label="Mit Eingabe-Taste auswählen"
                selected-label="Ausgewählt"
                deselect-label="Mit Eingabe-Taste abwählen"
                :disabled="ignore_project_operation_assignment"
              >
                <template v-slot:noResult>
                  Keine Suchergebnisse.
                </template>
                <template v-slot:noOptions>
                  Liste ist leer.
                </template>
              </Multiselect>

              <span v-if="ignore_project_operation_assignment">
                <b>Projekte und Tätigkeiten werden automatisch allen Mitarbeitern angezeigt. </b>
              </span>
            </v-form>
            <v-progress-linear
              v-if="saving"
              indeterminate
            />
            <v-alert type="error" :value="!!errors.validation.detail">
              {{ errors.validation.detail }}
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              text
              :disabled="saving"
              @click="$router.push({name: 'projects'})"
            >
              Abbrechen
            </v-btn>
            <v-btn
              text
              :disabled="saving"
              @click="saveEditDialog"
            >
              Speichern
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- confirm removal -->
      <v-dialog
        v-model="showDeleteDialog"
        :max-width="dialogWidth"
      >
        <v-card>
          <v-card-title><span class="headline">Projekt wirklich löschen?</span></v-card-title>
          <v-card-text>
            <p>Möchten Sie das Projekt <em>{{ selectedObject.name }}</em> wirklich löschen?</p>
            <p>Achtung: Dadurch werden alle Stundeneinträge, die diesem Projekt zugeordnet sind, unwiderruflich gelöscht.</p>
            <v-alert type="error" :value="!!errors.validation.detail">
              {{ errors.validation.detail }}
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              text
              :disabled="saving"
              @click="showDeleteDialog = false"
            >
              Abbrechen
            </v-btn>
            <v-btn
              text
              :disabled="saving"
              @click="deleteProject"
            >
              Löschen
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-toolbar-items>
        <v-btn
          icon
          :disabled="loading"
          @click="fetchItems"
        >
          <v-icon>mdi-cached</v-icon>
        </v-btn>
        <v-btn
          icon
          :disabled="loading"
          @click="openNewDialog"
        >
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </v-toolbar-items>
    </v-toolbar>

    <!-- actual data -->
    <v-data-table
      :headers="headers"
      :items-per-page="-1"
      :items="items"
      :loading="loading"
      fixed-header
      :height="dataTableHeight"
    >
      <template v-slot:item="props">
        <tr>
          <td>{{ props.item.name }}</td>
          <td>
            <v-icon
              v-if="props.item.inactive"
              small
            >
              mdi-checkbox-outline
            </v-icon>
            <v-icon
              v-else
              small
            >
              mdi-checkbox-blank-outline
            </v-icon>
          </td>
          <td class="text-right">
            <v-btn
              icon
              title="Bearbeiten"
              :disabled="loading || showEditDialog"
              @click="openEditDialog(props.item)"
            >
              <v-icon small>
                mdi-pencil
              </v-icon>
            </v-btn>
            <v-btn
              icon
              title="Löschen"
              :disabled="loading"
              @click="openDeleteDialog(props.item)"
            >
              <v-icon small>
                mdi-delete
              </v-icon>
            </v-btn>
          </td>
        </tr>
      </template>
      <template v-slot:no-data>
        <v-alert type="error" :value="errors.unknown">{{ errors.unknown }}</v-alert>
        <span>Keine Daten.</span>
      </template>
    </v-data-table>
  </v-flex>
</template>

<script>
import { mapGetters } from 'vuex'
import { api, endpoints } from '@/api'
import Multiselect from 'vue-multiselect'

import {getEmployeeLabel} from '../filters/Employee'
import {dialogWidth, dataTableHeight} from './utils/sizes'

export default {
  metaInfo: {
    title: 'Projekte verwalten',
  },
  components: {
    Multiselect,
  },
  data () {
    return {
      headers: [
        { text: "Name", value: "name" },
        { text: "Inaktiv", value: "inactive", sortable: false },
        { text: "Aktion", value: "id", sortable: false, class: "text-right" },
      ],
      items: [],
      errors: {
        validation: {},
        unknown: "",
      },
      showEditDialog: false,
      showDeleteDialog: false,
      selectedObject: {
        id: null,
        name: "",
        inactive: false,
        employees: [],
        operations: [],
      },
      saving: false,
      allEmployees: [],
      employeesLoading: false,
      allOperations: [],
      operationsLoading: false,
      loadingSimilarItems: false,
      similarItems: [],
      dataTableHeight: dataTableHeight
    }
  },
  computed: {
    ...mapGetters(['selectedTenant', 'selectedTenantObj', 'profile']),
    dialogWidth: dialogWidth,
    selectedProjectId () {
      return this.$route.params.id || null
    },
    itemFilters () {
      const r = {
        tenant: this.selectedTenant,
        details: false,
        ordering: "name",
      }
      if (!this.profile.show_inactive_projects) {
        r.inactive = false
      }
      return r
    },
    employeeFilter () {
      const r = {tenants: this.selectedTenant}
      if (!this.profile.show_inactive_employees) {
        r.inactive = false
      }
      return r
    },
    ignore_project_operation_assignment () {
      return this.selectedTenantObj && this.selectedTenantObj.ignore_project_operation_assignment
    },
  },
  watch: {
    selectedTenant: {
      handler () {
        this.fetchItems()
        this.fetchEmployees()
        this.fetchOperations()
      },
      immediate: true,
    },
    'profile.show_inactive_projects' () {
      this.fetchItems()
    },
    'profile.show_inactive_employees' () {
      this.fetchEmployees()
    },
    selectedProjectId: {
      async handler (newValue) {
        if (newValue === null) {
          this.showEditDialog = false
          return
        }
        if (newValue === "new") {
          this.selectedObject.id = null
          this.selectedObject.name = ""
          this.selectedObject.inactive = false
          this.selectedObject.employees.splice(0)
          this.selectedObject.operations.splice(0)
          this.$set(this.errors, "validation", {})
          this.showEditDialog = true
        } else {
          this.loading = true
          try {
            const r = await api(endpoints.fetch_project(this.selectedProjectId))
            if (r.ok) {
              const project = await r.json()
              this.selectedObject.id = project.id
              this.selectedObject.name = project.name
              this.selectedObject.inactive = project.inactive
              this.selectedObject.employees.splice(0, this.selectedObject.employees.length, ...project.employees)
              this.selectedObject.operations.splice(0, this.selectedObject.operations.length, ...project.operations)
              this.$set(this.errors, "validation", {})
              this.showEditDialog = true
            }
            else if (r.status >= 400 && r.status < 500) {
              this.$router.push({name: "projects"})
              let response_json = await r.json()
              alert(response_json["detail"] || "Unbekannter Fehler")
            }
            else if (r.status >= 500){
              this.$router.push({name: "projects"})
              alert("Serverfehler")
            }
          } catch (err) {
            // TODO: Error handling
            console.log(err)
          } finally {
            this.loading = false
          }
        }
      },
      immediate: true,
    },
    "selectedObject.name" : {
      handler (newValue) {
        this.fetchSimilarProjects()
      },
      immediate: true,
    },
  },
  methods: {
    async fetchItems () {
      this.loading = true
      this.errors.unknown = ""
      if (this.selectedTenant === null) {
        return
      }
      try {
        const r = await api(endpoints.fetch_project(), this.itemFilters, {method: 'GET'})
        if (r.ok) {
          const d = await r.json()
          this.items.splice(0, this.items.length, ...d)
        } else {
          this.items.splice(0)
          this.errors.unknown = (await r.json()).detail
        }
      } catch (err) {
        console.log(err)
        this.items.splice(0)
        this.errors.unknown = err.message
      } finally {
        this.loading = false
      }
    },
    openNewDialog () {
      this.$router.push({name: "projects", params: {id: "new"}})
    },
    openEditDialog (project) {
      this.$router.push({name: "projects", params: {id: project.id}})
    },
    openDeleteDialog (project) {
      this.selectedObject.id = project.id
      this.selectedObject.name = project.name
      this.$set(this.errors, "validation", {})
      this.showDeleteDialog = true
    },
    async saveEditDialog () {
      this.saving = true
      const data = {
        name: this.selectedObject.name,
        inactive: this.selectedObject.inactive,
        employees: this.selectedObject.employees,
        operations: this.selectedObject.operations,
      }
      const isNewObject = this.selectedObject.id === null
      if (isNewObject) {
        data.tenant = this.selectedTenant
      }
      try {
        const r =
          !isNewObject ?
          await api(endpoints.update_project(this.selectedObject.id), data, {method: "PATCH"}) :
          await api(endpoints.create_project(), data, {method: "POST"})
        if (r.ok) {
          this.$router.push({name: "projects"})
          await this.fetchItems()
          this.$root.$emit("projectsChanged")
        } else {
          // TODO: error handling
          this.$set(this.errors, "validation", await r.json())
        }
      } catch (err) {
        // TODO: error handling
      } finally {
        this.saving = false
      }
    },
    async deleteProject () {
      this.saving = true
      try {
        const r = await api(endpoints.delete_project(this.selectedObject.id), undefined, {method: "DELETE"})
        if (r.ok) {
          this.showDeleteDialog = false
          await this.fetchItems()
          this.$root.$emit("projectsChanged")
        } else {
          // TODO: error handling
          this.$set(this.errors, "validation", await r.json())
        }
      } catch (err) {
        // TODO: error handling
      } finally {
        this.saving = false
      }
    },
    getError (path) {
      const parts = path.split(".")
      let obj = this.errors.validation
      while (parts.length > 0) {
        const key = parts.splice(0, 1)
        obj = obj[key]
        if (typeof obj === "undefined") {
          return obj
        }
      }
      return obj
    },
    async fetchEmployees () {
      if (this.selectedTenant === null) {
        return
      }
      this.employeesLoading = true
      try {
        const r = await api(endpoints.fetch_employee(), this.employeeFilter, {method: "GET"})
        if (r.ok) {
          const d = await r.json()
          this.allEmployees.splice(0, this.allEmployees.length, ...d)
        } else {
          // TODO: error handling
        }
      } catch (err) {
          // TODO: error handling
      } finally {
        this.employeesLoading = false
      }
    },
    async fetchOperations () {
      if (this.selectedTenant === null) {
        return
      }
      this.operationsLoading = true
      try {
        const r = await api(endpoints.fetch_operation(), {tenant: this.selectedTenant, ordering: "name"}, {method: "GET"})
        if (r.ok) {
          const d = await r.json()
          this.allOperations.splice(0, this.allOperations.length, ...d)
        } else {
          // TODO: error handling
        }
      } catch (err) {
          // TODO: error handling
      } finally {
        this.operationsLoading = false
      }
    },
    getEmployeeLabel: getEmployeeLabel,
    async fetchSimilarProjects () {
      if (this.selectedTenant === null) {
        return
      }

      this.loadingSimilarItems = true
      this.errors.unknown = ""

      try {
        let name = this.selectedObject.name
        if(name === null || name.length < 3){
          this.similarItems.splice(0)
        }
        else{
          let nameFilter = {'search': this.selectedObject.name}
          const r = await api(endpoints.fetch_project(),  {...nameFilter, ...this.itemFilters}, {method: 'GET'})
          if (r.ok) {
            const d = (await r.json()).filter(item => item.id !== this.selectedObject.id)
            this.similarItems.splice(0, this.similarItems.length, ...d)
          } else {
            this.similarItems.splice(0)
            this.errors.unknown = (await r.json()).detail
          }
        }
      } catch (err) {
        console.log(err)
        this.similarItems.splice(0)
        this.errors.unknown = err.message
      } finally {
        this.loadingSimilarItems = false
      }
    },
  },
}
</script>
<style lang="scss" scoped>
  .v-list {
  max-height: 200px;
  overflow-y: auto;
}

</style>
